import { getAfterAuthPagePath } from '@/fsd/entities/auth/lib/helpers/getAfterAuthPagePath';
import { CabinetApi } from '@/fsd/entities/cabinet/api';
import { cabinetHandleRefresh } from '@/fsd/entities/cabinet/lib/helpers/cabinetHandlerRefresh';
import { ICabinetModalType } from '@/fsd/entities/cabinet/model/types/ICabinetModalType';
import { CabinetModal } from '@/fsd/entities/cabinet/ui/CabinetModal/CabinetModal';
import {
  ICurrentCabinetData,
  OwnCabinet,
} from '@/fsd/feature/toggle-cabinet/models';
import { routes } from '@/fsd/shared/config/routes/routesConfig';
import { Button, RadioButton } from '@/fsd/shared/ui';
import EmptyData from '@/fsd/shared/ui/EmptyData/EmptyData';
import FormField from '@/fsd/shared/ui/form-field/FormField';
import PlusIcon from '@/fsd/shared/ui/icons/plus';
import Loader from '@/fsd/shared/ui/Loader/Loader';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import style from './ChooseCabinet.module.scss';

const ChooseCabinet = observer(() => {
  const navigate = useNavigate();

  const typeModal = useRef<ICabinetModalType>('create');

  const [openModal, setOpenModal] = useState(false);
  const [selectedCabinetId, setSelectedCabinetId] = useState<number>();
  const [cabinets, setCabinets] = useState<OwnCabinet[]>([]);
  const [newCabinetName, setNewCabinetName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const fetchCabinets = async () => {
    const { load, current } = CabinetApi;
    setIsLoading(true);

    try {
      const cabinetsData = await load();
      const newCabinets: OwnCabinet[] = [
        ...(cabinetsData?.data.member_cabinets?.map(
          (cabinet) => cabinet.cabinet
        ) || []),
        ...(cabinetsData?.data.own_cabinets?.map((cabinet) => cabinet) || []),
      ];
      setCabinets(newCabinets);

      const currentData = await current();
      const afterAuthPagePath = await getAfterAuthPagePath();

      if (currentData?.data.cabinet_id) navigate(afterAuthPagePath);

      setSelectedCabinetId(currentData?.data.cabinet_id);
    } catch (error) {
      console.error('Error fetching cabinets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCabinets();
  }, []);

  const handleCabinetChange = (id: number) => {
    setSelectedCabinetId(id);
    CabinetApi.switch(id);
  };

  const handleContinue = () => {
    navigate(routes.account.path);
  };

  const handleCreateCabinet = async () => {
    await CabinetApi.create(newCabinetName);
    handleCloseModal();
    await fetchCabinets();
    navigate(routes.account.path);
    cabinetHandleRefresh();
  };


  if (isLoading)
    return <EmptyData className={style.emptyData} desc={<Loader />} />;

  return (
    <main className={style.main}>
      <form className={style.mainForm}>
        <div className={style.header}>
          <div className={style.mainFormTitle}>Выберите кабинет</div>
        </div>
        <div className={style.inputContainer}>
          <FormField>
            <div className={style.cabinetButtons}>
              {cabinets.length > 0 ? (
                cabinets.map((cabinet) => (
                  <RadioButton
                    key={cabinet.id}
                    className={style.radioButton}
                    id={String(cabinet.id)}
                    name="cabinet"
                    label={
                      <div className={style.radioButtonLabel}>
                        {cabinet.name}
                        <span className={style.radioButtonLabelEmail}>
                          {cabinet.owner}
                        </span>
                      </div>
                    }
                    checked={selectedCabinetId === cabinet.id}
                    onChange={() => handleCabinetChange(cabinet.id)}
                  />
                ))
              ) : (
                <div className={style.noCabinetsMessage}>
                  Нет доступных кабинетов
                </div>
              )}
            </div>
          </FormField>

          <Button
            className={style.addCabinetBtn}
            variant="outline"
            type="button"
            onClick={handleOpenModal}
          >
            <PlusIcon />
            Добавить кабинет
          </Button>
        </div>

        <div className={style.footerButtons}>
          <Button
            variant="secondary"
            type="button"
            onClick={() => window.history.back()}
          >
            Назад
          </Button>
          <Button
            className={style.AccountBtn}
            type="button"
            onClick={handleContinue}
            disabled={cabinets.length === 0}
          >
            Войти
          </Button>
        </div>
      </form>

      <CabinetModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        typeModal={typeModal}
        titleText="Новый кабинет"
        newCabinetName={newCabinetName}
        setNewCabinetName={setNewCabinetName}
        handleCreateCabinet={handleCreateCabinet}
      />

      <a href={routes.home.path}>Вернуться на главную</a>
    </main>
  );
});

export default ChooseCabinet;
